import React, { useMemo, useEffect, useRef } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Chip,
  Stack,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from "@mui/material";

import { useBuildLogs } from "src/domains/application/hooks/useBuildLogs";
import { useDeploymentStream } from "src/domains/application/hooks/useDeploymentStream";

import {
  deriveCIJobSteps,
  deriveEventSteps,
  deriveLiveLogSteps,
  deriveServiceBuildStep,
  TaskStepsList,
  type TaskStep,
} from "./taskSteps";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface PhaseDetailViewProps {
  stackAlias: string;
  templateSlug?: string;
  deployMode?: string;
  runId: string;
  phase: string; // "build" | "deploy"
  selectedJob: string | null; // service name (controlled by StagesSidebar jobs mode)
  /** Export the jobs list so the parent (PipelineTab) can feed it to StagesSidebar */
  onJobsUpdate?: (
    jobs: Array<{
      name: string;
      status: string;
      message: string;
      startedAt: string | null;
      completedAt: string | null;
    }>,
  ) => void;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_CHIP_COLOR: Record<
  string,
  "success" | "error" | "warning" | "info" | "default"
> = {
  completed: "success",
  in_progress: "info",
  failed: "error",
  pending: "warning",
  queued: "default",
};

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const STATUS_ICON: Record<string, React.ReactElement> = {
  completed: (
    <CheckCircleIcon fontSize="small" sx={{ color: "success.main" }} />
  ),
  in_progress: (
    <SyncIcon
      fontSize="small"
      sx={{ color: "info.main", animation: `${spin} 1.5s linear infinite` }}
    />
  ),
  failed: <ErrorIcon fontSize="small" sx={{ color: "error.main" }} />,
  pending: (
    <HourglassEmptyIcon fontSize="small" sx={{ color: "warning.main" }} />
  ),
  queued: <HourglassEmptyIcon fontSize="small" sx={{ color: "grey.500" }} />,
};

// ── Component ──────────────────────────────────────────────────────────────────

export function PhaseDetailView({
  stackAlias,
  templateSlug: _templateSlug,
  deployMode,
  runId,
  phase,
  selectedJob,
  onJobsUpdate,
}: PhaseDetailViewProps): React.ReactElement {
  const theme = useTheme();

  // Deployment stream data
  const {
    events,
    pipelineState,
    logBatches,
    connected: _connected,
  } = useDeploymentStream(stackAlias);

  // Scope to the selected run
  const scopedPipelineState = useMemo(() => {
    if (!pipelineState || pipelineState.length === 0) return [];
    return pipelineState.filter((s) => s.sagaId === runId);
  }, [pipelineState, runId]);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => e.sagaId === runId);
  }, [events, runId]);

  // Get jobs (services) for this phase from pipeline state
  const jobs = useMemo(() => {
    const phaseRows = scopedPipelineState.filter(
      (s) => s.step === phase && s.serviceName !== "",
    );
    return phaseRows.map((s) => ({
      name: s.serviceName,
      status: s.status,
      message: s.message,
      startedAt: s.startedAt,
      completedAt: s.completedAt,
    }));
  }, [scopedPipelineState, phase]);

  // Also get event-derived jobs for when pipeline state has no service-level rows
  const eventDerivedJobs = useMemo(() => {
    if (jobs.length > 0) return [];
    const phaseEvents = filteredEvents.filter((e) => e.step === phase);
    const serviceNames = new Set<string>();
    for (const e of phaseEvents) {
      const svc = (e.metadata?.serviceName as string) ?? "";
      if (svc) serviceNames.add(svc);
    }
    return Array.from(serviceNames).map((name) => {
      const svcEvents = phaseEvents.filter(
        (e) => (e.metadata?.serviceName as string) === name,
      );
      const latest = svcEvents[0];
      return {
        name,
        status: latest?.status ?? "pending",
        message: latest?.message ?? "",
        startedAt: null as string | null,
        completedAt: null as string | null,
      };
    });
  }, [jobs, filteredEvents, phase]);

  const allJobs = jobs.length > 0 ? jobs : eventDerivedJobs;

  // Export jobs to parent so StagesSidebar can show them
  useEffect(() => {
    onJobsUpdate?.(allJobs);
  }, [allJobs, onJobsUpdate]);

  // Resolve the selected job (default to first if not specified)
  const activeJob = useMemo(() => {
    if (selectedJob) {
      return allJobs.find((j) => j.name === selectedJob) ?? allJobs[0] ?? null;
    }
    return allJobs[0] ?? null;
  }, [selectedJob, allJobs]);

  // Scoped log batches for the selected run
  const scopedLogBatches = useMemo(() => {
    return logBatches.get(runId) ?? new Map<string, Map<string, string[]>>();
  }, [logBatches, runId]);

  // Build logs for the active job
  const isRushaManaged =
    deployMode === "rusha_managed" || deployMode === "rusha_build_ci_deploy";

  const serviceLogs = useBuildLogs(stackAlias, activeJob?.name, {
    autoFetch: false,
    pollWhileActive: phase === "build" && activeJob?.status === "in_progress",
    logBatches: scopedLogBatches,
    buildMode: isRushaManaged ? "rusha_managed" : "ci",
    sagaId: runId,
  });

  // Fetch build logs when the job changes
  const fetchedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeJob && phase === "build") {
      const key = `${runId}:${activeJob.name}:${phase}`;
      if (fetchedForRef.current !== key) {
        fetchedForRef.current = key;
        void serviceLogs.refetch();
      }
    } else {
      fetchedForRef.current = null;
    }
  }, [activeJob, runId, phase, serviceLogs.refetch]);

  // Step events for the active job (filtered by phase + service)
  const stepEvents = useMemo(() => {
    if (!activeJob) return [];
    return filteredEvents.filter((e) => {
      if (e.step !== phase) return false;
      const svc = (e.metadata?.serviceName as string) ?? "";
      return svc === activeJob.name;
    });
  }, [filteredEvents, phase, activeJob]);

  // Derive task steps for the active job — merge all sources instead of
  // picking one tier. This ensures both CI quality gates (test) and
  // Rusha-managed build steps appear together.
  const taskSteps = useMemo<TaskStep[]>(() => {
    const steps: TaskStep[] = [];

    if (phase === "build") {
      // 1. Quality gates (CI test/lint) from the provisioned ci.yml
      //    Filter to only gates matching the active service name
      if (serviceLogs.qualityGates && serviceLogs.qualityGates.length > 0) {
        const filteredGates = activeJob
          ? serviceLogs.qualityGates.filter(
              (qg) =>
                qg.jobName.endsWith(`-${activeJob.name}`) ||
                qg.jobName === activeJob.name,
            )
          : serviceLogs.qualityGates;
        if (filteredGates.length > 0) {
          steps.push(...deriveCIJobSteps([], filteredGates));
        }
      }

      // 2. Rusha-managed build step — from liveLogs (active build) or
      //    pipeline state (completed build)
      const hasLiveBuildLogs = !!(
        serviceLogs.liveLogs &&
        serviceLogs.liveLogs.size > 0 &&
        activeJob &&
        serviceLogs.liveLogs.get(activeJob.name)?.size
      );

      if (hasLiveBuildLogs) {
        const serviceLiveLogs = serviceLogs.liveLogs!.get(activeJob!.name)!;
        steps.push(...deriveLiveLogSteps(serviceLiveLogs, phase));
      } else if (activeJob) {
        // Completed build: synthesize a build step from the job's state
        steps.push(deriveServiceBuildStep(activeJob));
      }

      // 3. CI-managed build log jobs (non-quality-gate) — for ci_managed mode
      if (serviceLogs.logs && serviceLogs.logs.length > 0) {
        steps.push(...deriveCIJobSteps(serviceLogs.logs, []));
      }
    } else {
      // Deploy phase — use existing logic
      if (serviceLogs.liveLogs && serviceLogs.liveLogs.size > 0 && activeJob) {
        const serviceLiveLogs = serviceLogs.liveLogs.get(activeJob.name);
        if (serviceLiveLogs && serviceLiveLogs.size > 0) {
          steps.push(...deriveLiveLogSteps(serviceLiveLogs, phase));
        }
      }

      if (stepEvents.length > 0) {
        steps.push(...deriveEventSteps(stepEvents));
      }
    }

    return steps;
  }, [
    phase,
    serviceLogs.liveLogs,
    serviceLogs.logs,
    serviceLogs.qualityGates,
    stepEvents,
    activeJob,
  ]);

  // Phase label
  const phaseLabel = phase === "build" ? "Build" : "Deploy";

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        overflow: "auto",
        bgcolor: alpha(theme.palette.primary.main, 0.01),
        minHeight: 480,
      }}
    >
      {activeJob ? (
        <Stack>
          {/* Job header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {STATUS_ICON[activeJob.status]}
              <Typography variant="subtitle2" fontWeight={600}>
                {activeJob.name} · {phaseLabel}
              </Typography>
              <Chip
                label={activeJob.status.replace("_", " ")}
                size="small"
                color={STATUS_CHIP_COLOR[activeJob.status] ?? "default"}
              />
            </Stack>
          </Box>

          {/* Task steps with inline logs */}
          <Box sx={{ py: 1 }}>
            <TaskStepsList steps={taskSteps} />
          </Box>
        </Stack>
      ) : (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No jobs found for the {phaseLabel} phase.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
